import { useEffect, useState, useMemo, type FormEvent } from "react"
import { createPortal } from "react-dom"
import {
  Building2,
  ImagePlus,
  Loader2,
  Send,
  ClipboardList,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  Check,
  CheckCircle2,
  Clock3,
  Calendar,
  Package,
  X,
  FileText,
  User,
  Trash2,
  Camera,
  Sparkles,
  Eye,
  Wrench,
  Zap,
  FileEdit,
  Pencil,
  Plus,
} from "lucide-react"
import { useQueryClient } from "@tanstack/react-query"
import { supabase, Property, CleanReport } from "../lib/supabase"
import { MediaCaptureModal } from "./MediaCaptureModal"
import { SupplySnapModal } from "./SupplySnapModal"
import { SiteSenseBlock } from "./SiteSenseBlock"
import { useAuth } from "./AuthProvider"
import { isDemoSite } from "../lib/isDemoSite"
import { formatRelativeDate } from "../lib/relativeDate"
import { createEmptyChecklist, countCompletedTasks, countTotalTasks, type ChecklistData } from "../lib/checklistTemplate"
import { ChecklistEditModal } from "./ChecklistEditModal"
import { useToast } from "./Toast"
import { useCompanyConfig } from "../lib/useCompanyConfig"
import { useGlassTokens } from "../lib/useAppBackground"
import { GlitchSelect } from "./GlitchSelect"
import { GlitchDatePicker } from "./GlitchDatePicker"
import { RichTextEditor } from "./RichTextEditor"
import { GlitchWord } from "./GlitchWord"
import { PropertySelector, type PropertyOption as PropertySelectorOption } from "./PropertySelector"
import { TemplateEditorModal } from "./TemplateEditorModal"
import { ActivityTypesModal } from "./ActivityTypesModal"

// ── Activity templates ─────────────────────────────────────────────────────

interface ActivityTemplate {
  id: string
  label: string
  description: string
  icon: React.ComponentType<{ className?: string }>
  serviceType?: string
  checklist?: ChecklistData
  color: string
}

function tpl(tasks: string[]): { tasks: { task: string; completed: boolean }[] } {
  return { tasks: tasks.map((task) => ({ task, completed: false })) }
}

// IF-426 TODO: keep this as bootstrap defaults until templates are moved to a
// first-class `activity_templates` model (single source of truth).
const BUILT_IN_TEMPLATES: ActivityTemplate[] = [
  {
    id: "standard-clean",
    label: "Standard Clean",
    description: "Regular maintenance service — full checklist, photos, supply check.",
    icon: Sparkles,
    serviceType: "Standard Maintenance",
    checklist: {
      "General Areas": tpl(["Dust all surfaces", "Wipe door handles & light switches", "Empty trash bins", "Replace trash bags"]),
      "Floors": tpl(["Vacuum carpeted areas", "Mop hard floors", "Spot clean stains"]),
      "Restrooms": tpl(["Clean and disinfect toilets", "Clean sinks and mirrors", "Restock supplies", "Mop floors"]),
      "Kitchen / Break Room": tpl(["Clean countertops", "Clean sink", "Wipe appliance exteriors", "Empty trash"]),
    },
    color: "teal",
  },
  {
    id: "deep-clean",
    label: "Deep Clean",
    description: "Intensive deep service with detailed room-by-room protocol.",
    icon: Zap,
    serviceType: "Deep Clean",
    checklist: {
      "Primary Areas": tpl(["Scrub all hard floors", "Deep clean carpets", "Clean all baseboards", "Wash interior windows", "Clean light fixtures and covers"]),
      "Kitchens & Break Rooms": tpl(["Degrease all appliances", "Clean inside cabinets", "Descale faucets and fixtures", "Deep clean sink and drain"]),
      "Restrooms": tpl(["Descale all fixtures", "Deep scrub tile and grout", "Disinfect all surfaces", "Clean ventilation covers"]),
      "Detail Work": tpl(["Clean all door frames and handles", "Wipe all switch plates and outlets", "Dust ceiling corners and vents", "Clean behind and under furniture"]),
    },
    color: "blue",
  },
  {
    id: "inspection",
    label: "Inspection",
    description: "Walkthrough inspection — photos and notes only, no checklist.",
    icon: Eye,
    serviceType: "Inspection",
    checklist: {
      "Walkthrough": tpl(["Inspect entrance and common areas", "Inspect restrooms", "Inspect kitchens/break rooms", "Inspect offices/workspaces", "Note any maintenance issues", "Photo-document findings"]),
    },
    color: "amber",
  },
  {
    id: "maintenance",
    label: "Maintenance",
    description: "Repair or maintenance task — focused on specific issues.",
    icon: Wrench,
    serviceType: "Maintenance",
    checklist: {
      "Task": tpl(["Identify issue", "Complete repair/maintenance", "Verify fix", "Clean work area", "Document completion"]),
    },
    color: "peach",
  },
  {
    id: "blank",
    label: "Blank Activity",
    description: "Start from scratch — fill in everything manually.",
    icon: FileEdit,
    color: "white",
  },
]

type OperatorOption = { id: string; email: string; full_name: string | null; displayName: string }
/** Property list item: base fields + optional client for admin dropdown. */
type PropertyOption = Pick<Property, "id" | "name" | "address"> & { client?: { name: string } | { name: string }[] }

const STATUS_PREVIEW_CLASSES: Record<string, string> = {
  Completed: "bg-emerald-500/20 text-emerald-200 border-emerald-400/30",
  "In Progress": "bg-blue-500/20 text-blue-200 border-blue-400/30",
  Scheduled: "bg-amber-500/20 text-amber-200 border-amber-400/30",
}

export type NewActivityFormMode = "operator" | "admin"

export interface NewActivityFormProps {
  mode: NewActivityFormMode
  onSuccess?: () => void
  onCancel?: () => void
}

export function NewActivityForm({
  mode,
  onSuccess,
  onCancel,
}: NewActivityFormProps) {
  const glass = useGlassTokens()
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const { reportLabel, config } = useCompanyConfig()
  const { user, companyId, operatorId, isCreator = false, isSuperManager = false, allowedClientIds = [], canWrite = false } = useAuth()
  const availableServiceTypes = config.service_types.length > 0 ? config.service_types : ["General Service"]
  const effectiveFullScope = isCreator || isSuperManager
  const isAdmin = mode === "admin"

  // Template management: creator + super manager only (per-admin scopes coming in Users page)
  const canManageTemplates = isCreator || isSuperManager
  const canManageActivityTypes = isAdmin && canWrite

  // Step 1: template selection → Step 2: form
  const [step, setStep] = useState<"template" | "form">(isAdmin ? "template" : "form")
  const [selectedTemplate, setSelectedTemplate] = useState<ActivityTemplate | null>(null)

  // Template editor state
  const [editingTemplate, setEditingTemplate] = useState<ActivityTemplate | null>(null)
  const [creatingNewTemplate, setCreatingNewTemplate] = useState(false)
  const [activityTypesModalOpen, setActivityTypesModalOpen] = useState(false)
  const [addingType, setAddingType] = useState(false)
  const [newTypeDraft, setNewTypeDraft] = useState("")
  const [savingType, setSavingType] = useState(false)

  // IF-426 TODO: swap this merge to read from unified Activity Templates
  // storage while preserving these defaults as seed/fallback.
  // Merge built-in templates with company-configured checklist templates
  const templates = useMemo<ActivityTemplate[]>(() => {
    const extra: ActivityTemplate[] = []
    if (config.checklist_templates) {
      for (const [name, rawEntry] of Object.entries(config.checklist_templates)) {
        if (BUILT_IN_TEMPLATES.some((t) => t.id === name)) continue
        let cl: ChecklistData
        let st: string | undefined
        if (rawEntry && typeof rawEntry === "object" && "checklist" in rawEntry && typeof (rawEntry as Record<string, unknown>).checklist === "object") {
          const wrapped = rawEntry as { checklist: ChecklistData; service_type?: string }
          cl = wrapped.checklist
          st = wrapped.service_type
        } else {
          cl = rawEntry as ChecklistData
        }
        extra.push({
          id: `custom-${name}`,
          label: name,
          description: `Company template: ${name}`,
          icon: ClipboardList,
          checklist: cl,
          serviceType: st,
          color: "luster",
        })
      }
    }
    return [...BUILT_IN_TEMPLATES, ...extra]
  }, [config.checklist_templates])

  function handleTemplateSelect(t: ActivityTemplate) {
    setSelectedTemplate(t)
    if (t.serviceType) {
      setServiceType(t.serviceType)
    } else {
      setServiceType(availableServiceTypes[0] ?? "")
    }
    if (t.checklist) {
      setChecklist(JSON.parse(JSON.stringify(t.checklist)))
      setProtocolChecklist(JSON.parse(JSON.stringify(t.checklist)))
      setOpenCategories({})
    } else {
      setChecklist(createEmptyChecklist())
      setProtocolChecklist(null)
      setOpenCategories({})
    }
    setNotes("")
    setStep("form")
  }

  async function saveCustomTemplate(name: string, serviceType: string, cl: ChecklistData) {
    if (!companyId) { toast("No company context — can't save template.", "error"); return }
    const { data: existing } = await supabase
      .from("company_config")
      .select("config")
      .eq("company_id", companyId)
      .maybeSingle()
    const prev = typeof existing?.config === "object" && existing.config !== null
      ? existing.config as Record<string, unknown>
      : {}
    const prevTemplates = (prev.checklist_templates ?? {}) as Record<string, unknown>
    const merged = { ...prev, checklist_templates: { ...prevTemplates, [name]: serviceType ? { checklist: cl, service_type: serviceType } : cl } }
    const { error } = await supabase
      .from("company_config")
      .upsert({ company_id: companyId, config: merged as unknown as import("../src/types/database.types").Json, updated_at: new Date().toISOString() })
    if (error) { toast(error.message, "error"); throw error }
    queryClient.invalidateQueries({ queryKey: ["company-config"] })
    toast(`Template "${name}" saved.`, "success")
    setEditingTemplate(null)
    setCreatingNewTemplate(false)
  }

  async function deleteCustomTemplate(name: string) {
    if (!companyId) return
    const { data: existing } = await supabase
      .from("company_config")
      .select("config")
      .eq("company_id", companyId)
      .maybeSingle()
    const prev = typeof existing?.config === "object" && existing.config !== null
      ? existing.config as Record<string, unknown>
      : {}
    const prevTemplates = { ...((prev.checklist_templates ?? {}) as Record<string, ChecklistData>) }
    delete prevTemplates[name]
    const merged = { ...prev, checklist_templates: prevTemplates }
    const { error } = await supabase
      .from("company_config")
      .upsert({ company_id: companyId, config: merged, updated_at: new Date().toISOString() })
    if (error) { toast(error.message, "error"); throw error }
    queryClient.invalidateQueries({ queryKey: ["company-config"] })
    toast(`Template "${name}" deleted.`, "success")
    setEditingTemplate(null)
  }

  async function saveActivityTypes(nextTypes: string[]) {
    if (!companyId) {
      toast("No company context — can't save activity types.", "error")
      return
    }
    const { data: existing } = await supabase
      .from("company_config")
      .select("config")
      .eq("company_id", companyId)
      .maybeSingle()
    const prev = typeof existing?.config === "object" && existing.config !== null
      ? (existing.config as Record<string, unknown>)
      : {}
    const merged = { ...prev, service_types: nextTypes }
    const { error } = await supabase
      .from("company_config")
      .upsert({
        company_id: companyId,
        config: merged as unknown as import("../src/types/database.types").Json,
        updated_at: new Date().toISOString(),
      })
    if (error) {
      toast(error.message, "error")
      throw error
    }
    queryClient.invalidateQueries({ queryKey: ["company-config"] })
    if (nextTypes.length > 0 && serviceType && !nextTypes.includes(serviceType)) {
      setServiceType(nextTypes[0])
    }
    toast("Activity types updated.", "success")
  }

  async function handleAddType() {
    const trimmed = newTypeDraft.trim()
    if (!trimmed || availableServiceTypes.includes(trimmed)) return
    setSavingType(true)
    try {
      await saveActivityTypes([...availableServiceTypes, trimmed])
      setServiceType(trimmed)
    } finally {
      setNewTypeDraft("")
      setAddingType(false)
      setSavingType(false)
    }
  }

  const [properties, setProperties] = useState<PropertyOption[]>([])
  const [propertySelectorOptions, setPropertySelectorOptions] = useState<PropertySelectorOption[]>([])
  const [clientOptions, setClientOptions] = useState<{ id: string; name: string; propertyCount: number }[]>([])
  const [operators, setOperators] = useState<OperatorOption[]>([])
  const [propertyId, setPropertyId] = useState("")
  const [notes, setNotes] = useState("")
  const [photos, setPhotos] = useState<string[]>([])
  const [checklist, setChecklist] = useState<ChecklistData>(() => createEmptyChecklist())
  const [protocolChecklist, setProtocolChecklist] = useState<ChecklistData | null>(null)
  const [openCategories, setOpenCategories] = useState<Record<string, boolean>>({})
  const [mediaModalOpen, setMediaModalOpen] = useState(false)
  const [supplySnapOpen, setSupplySnapOpen] = useState(false)
  const [supplySnapPhotos, setSupplySnapPhotos] = useState<string[]>([])
  const [supplySnapEstimate, setSupplySnapEstimate] = useState<number | null>(null)
  const [supplySnapRoom, setSupplySnapRoom] = useState("")
  const [siteSenseAudioUrl, setSiteSenseAudioUrl] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [supplySiteModalOpen, setSupplySiteModalOpen] = useState(false)
  const [photosModalOpen, setPhotosModalOpen] = useState(false)
  const [checklistModalOpen, setChecklistModalOpen] = useState(false)

  // Admin-only fields
  const [reportTitle, setReportTitle] = useState("")
  const [serviceType, setServiceType] = useState<CleanReport["service_type"]>("")
  const [cleanDate, setCleanDate] = useState(() => new Date().toISOString().split("T")[0])
  const [status, setStatus] = useState<CleanReport["status"]>("Scheduled")
  const [operatorChoice, setOperatorChoice] = useState<"none" | string | "other">("none")
  const [operatorNameCustom, setOperatorNameCustom] = useState("")

  const templateLocksServiceType = Boolean(selectedTemplate?.serviceType)

  // Only auto-fill service type for Blank / no-template (when user must pick)
  useEffect(() => {
    if (!templateLocksServiceType && !serviceType && availableServiceTypes.length > 0) {
      setServiceType(availableServiceTypes[0])
    }
  }, [availableServiceTypes, serviceType, templateLocksServiceType])

  const categoryKeys = useMemo(() => Object.keys(checklist), [checklist])
  const defaultOpen = useMemo(() => {
    const o: Record<string, boolean> = {}
    categoryKeys.forEach((cat, i) => { o[cat] = i === 0 })
    return o
  }, [categoryKeys])
  const isCategoryOpen = (cat: string) => openCategories[cat] ?? defaultOpen[cat] ?? false
  const setAllOpen = (open: boolean) => setOpenCategories(categoryKeys.reduce((acc, c) => ({ ...acc, [c]: open }), {}))

  useEffect(() => {
    async function load() {
      try {
        if (isAdmin) {
          let propsQuery = supabase
            .from("properties")
            .select("id, name, address, client_id, client:clients(name)")
            .order("name")
          if (effectiveFullScope) {
            const { data: scopeClients } = await supabase.from("clients").select("id").eq("is_demo", isDemoSite())
            const cids = (scopeClients || []).map((c) => c.id)
            propsQuery = cids.length > 0 ? propsQuery.in("client_id", cids) : propsQuery.in("client_id", [])
          } else if (allowedClientIds.length > 0) {
            propsQuery = propsQuery.in("client_id", allowedClientIds)
          } else {
            setProperties([])
            setLoading(false)
            return
          }
          const { data: propsData } = await propsQuery
          const propsList = (propsData ?? []) as unknown as PropertyOption[]
          setProperties(propsList)

          // Build PropertySelector-compatible options with client grouping
          const selectorOpts: PropertySelectorOption[] = propsList.map((p) => {
            const clientName = Array.isArray(p.client) ? p.client[0]?.name : (p.client as { name: string } | undefined)?.name
            return { id: p.id, name: p.name, clientId: (p as any).client_id ?? undefined, clientName: clientName ?? undefined }
          })
          setPropertySelectorOptions(selectorOpts)
          const byClientMap = new Map<string, { name: string; count: number }>()
          selectorOpts.forEach((p) => {
            if (!p.clientId || !p.clientName) return
            const cur = byClientMap.get(p.clientId)
            if (!cur) byClientMap.set(p.clientId, { name: p.clientName, count: 1 })
            else cur.count += 1
          })
          setClientOptions(Array.from(byClientMap.entries()).map(([id, { name, count }]) => ({ id, name, propertyCount: count })))

          const { data: operatorsData } = await (supabase as any)
            .from("portal_operators")
            .select("id, email, full_name")
            .order("full_name")
          setOperators(
            (operatorsData || []).map((c: any) => ({
              id: c.id,
              email: c.email,
              full_name: c.full_name,
              displayName: (c.full_name || c.email).trim() || c.email,
            }))
          )

          let preSelectId: string | null = null
          if (typeof window !== "undefined") {
            try {
              preSelectId = sessionStorage.getItem("luster_new_report_property_id")
              if (preSelectId) sessionStorage.removeItem("luster_new_report_property_id")
            } catch (_) {}
            if (!preSelectId) preSelectId = new URLSearchParams(window.location.search).get("property")
            if (preSelectId && propsList.some((p: PropertyOption) => p.id === preSelectId)) {
              setPropertyId(preSelectId)
            }
          }
        } else {
          const email = user?.email
          let allowedPropertyIds: string[] | null = null
          if (email) {
            const { data: operatorRow } = await (supabase as any)
              .from("portal_operators")
              .select("id")
              .eq("email", email)
              .maybeSingle()
            if (operatorRow) {
              const { data: links } = await (supabase as any)
                .from("portal_operator_properties")
                .select("property_id")
                .eq("operator_id", operatorRow.id)
              if (links && links.length > 0) {
                allowedPropertyIds = links.map((r: any) => r.property_id)
              }
            }
          }
          let query = supabase.from("properties").select("id, name, address").order("name")
          if (allowedPropertyIds !== null && allowedPropertyIds.length > 0) {
            query = query.in("id", allowedPropertyIds)
          } else {
            const { data: scopeClients } = await supabase.from("clients").select("id").eq("is_demo", isDemoSite())
            const cids = (scopeClients || []).map((c) => c.id)
            query = cids.length > 0 ? query.in("client_id", cids) : query.in("client_id", [])
          }
          const { data, error } = await query
          if (error) console.error("Failed to load properties for report:", error)
          const list = (data ?? []) as PropertyOption[]
          setProperties(list)
          setPropertySelectorOptions(list.map((p) => ({ id: p.id, name: p.name })))
          setClientOptions([])
          let preSelectId: string | null = null
          if (typeof window !== "undefined") {
            try {
              preSelectId = sessionStorage.getItem("luster_new_report_property_id")
              if (preSelectId) sessionStorage.removeItem("luster_new_report_property_id")
            } catch (_) {}
            if (!preSelectId) preSelectId = new URLSearchParams(window.location.search).get("property")
          }
          if (preSelectId && list.some((p) => p.id === preSelectId)) {
            setPropertyId(preSelectId)
          } else if (list.length === 1) {
            setPropertyId(list[0].id)
          }
        }
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [isAdmin, user?.email, effectiveFullScope, allowedClientIds])

  useEffect(() => {
    if (!propertyId) return
    let cancelled = false
    async function loadProtocol() {
      const { data: link } = await supabase
        .from("property_protocols")
        .select("protocol_id")
        .eq("property_id", propertyId)
        .limit(1)
        .maybeSingle()
      if (cancelled || !link) return
      const { data: proto } = await supabase
        .from("service_protocols")
        .select("categories")
        .eq("id", link.protocol_id)
        .single()
      if (cancelled || !proto?.categories) return
      const cats = proto.categories as Record<string, { tasks: { task: string }[] }>
      const asChecklist: ChecklistData = {}
      for (const [cat, data] of Object.entries(cats)) {
        asChecklist[cat] = { tasks: (data.tasks ?? []).map((t) => ({ task: t.task, completed: false })) }
      }
      if (Object.keys(asChecklist).length > 0) {
        setChecklist(asChecklist)
        setProtocolChecklist(asChecklist)
      }
    }
    loadProtocol()
    return () => { cancelled = true }
  }, [propertyId])

  function handlePhotosAdded(urls: string[]) {
    setPhotos((prev) => [...prev, ...urls])
  }

  function removePhoto(index: number) {
    setPhotos((prev) => prev.filter((_, i) => i !== index))
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    if (!propertyId) return
    setSaving(true)
    try {
      const property = properties.find((p) => p.id === propertyId)
      const reportTitleDefault = property ? `${property.name} – ${formatRelativeDate(cleanDate)}` : reportLabel
      const titleFinal =
        isAdmin && reportTitle.trim()
          ? reportTitle.trim()
          : reportTitleDefault
      const operatorName =
        isAdmin
          ? operatorChoice === "none"
            ? undefined
            : operatorChoice === "other"
              ? operatorNameCustom.trim() || undefined
              : operators.find((c) => c.id === operatorChoice)?.displayName
          : user?.user_metadata?.full_name ?? user?.email ?? "Operator"

      let timeEntryId: string | null = null
      if (operatorId && propertyId) {
        const { data: activeEntry } = await supabase
          .from("time_entries")
          .select("id")
          .eq("operator_id", operatorId)
          .eq("property_id", propertyId)
          .is("clock_out", null)
          .order("clock_in", { ascending: false })
          .limit(1)
          .maybeSingle()
        timeEntryId = activeEntry?.id ?? null
      }

      const payload = {
        property_id: propertyId,
        title: titleFinal,
        activity_date: cleanDate,
        status: isAdmin ? status : ("Completed" as const),
        service_type: isAdmin ? serviceType : ("Standard Maintenance" as const),
        operator_name: operatorName,
        checklist_notes: notes || null,
        checklist,
        photos: photos.length ? photos : null,
        supply_snap_photo: supplySnapPhotos.length ? JSON.stringify(supplySnapPhotos) : null,
        supply_snap_estimate: supplySnapEstimate ?? null,
        supply_snap_room: supplySnapRoom.trim() || null,
        site_sense_audio_url: siteSenseAudioUrl || null,
        ...(timeEntryId ? { time_entry_id: timeEntryId } : {}),
      }

      const { data: inserted, error } = await (supabase as any)
        .from("activities")
        .insert(payload)
        .select("id")
        .single()
      if (error) throw error

      if (inserted?.id) {
        supabase
          .functions.invoke("create-report-notifications", { body: { reportId: inserted.id } })
          .catch(() => {})
      }
      if (inserted?.id && (supplySnapPhotos.length > 0 || siteSenseAudioUrl)) {
        supabase
          .functions.invoke("analyze-supply-synapse", { body: { reportId: inserted.id } })
          .catch(() => {})
      }

      if (inserted?.id && checklist && companyId) {
        const total = countTotalTasks(checklist)
        const completed = countCompletedTasks(checklist)
        const failed = total - completed
        const threshold = config.checklist_failure_threshold ?? 3
        if (total > 0 && failed >= threshold) {
          const prop = properties.find((p) => p.id === propertyId)
          supabase.functions.invoke("trace-auto-incident", {
            body: {
              signal_type: "checklist_failure",
              company_id: companyId,
              metadata: {
                activity_id: inserted.id,
                property_id: propertyId,
                property_name: prop?.name ?? "a property",
                failed_count: failed,
                total_count: total,
                activity_date: cleanDate,
              },
            },
          }).catch(() => {})
        }
      }

      if (isAdmin && onSuccess) {
        onSuccess()
        return
      }

      setPropertyId("")
      setNotes("")
      setPhotos([])
      setChecklist(createEmptyChecklist())
      setProtocolChecklist(null)
      setOpenCategories({})
      setSupplySnapPhotos([])
      setSupplySnapEstimate(null)
      setSupplySnapRoom("")
      setSiteSenseAudioUrl(null)
      if (isAdmin) {
        setReportTitle("")
        setServiceType(availableServiceTypes[0] ?? "")
        setCleanDate(new Date().toISOString().split("T")[0])
        setStatus("Scheduled")
        setOperatorChoice("none")
        setOperatorNameCustom("")
        setSelectedTemplate(null)
        setStep("template")
      }
      toast("Activity submitted.", "success")
    } catch (err: unknown) {
      console.error("Submit report error:", err)
      const message =
        err && typeof err === "object" && "message" in err
          ? String((err as { message: unknown }).message)
          : err instanceof Error
            ? err.message
            : "Failed to submit report"
      toast(message, "error")
    } finally {
      setSaving(false)
    }
  }

  const uploadPrefix = isAdmin ? "draft" : "operator"

  if (loading && step === "form") {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <Loader2 className="w-8 h-8 animate-spin text-luster-500" />
      </div>
    )
  }

  const activityTypesModal = activityTypesModalOpen && canManageActivityTypes ? (
    <ActivityTypesModal
      types={availableServiceTypes}
      onClose={() => setActivityTypesModalOpen(false)}
      onSave={saveActivityTypes}
    />
  ) : null

  // ── Step 1: Template picker ────────────────────────────────────────────────

  if (step === "template" && isAdmin) {
    const TEMPLATE_COLORS: Record<string, { border: string; bg: string; text: string; icon: string }> = {
      teal:   { border: "border-teal-400/25",   bg: "bg-teal-500/10",   text: "text-teal-400",   icon: "text-teal-400" },
      blue:   { border: "border-blue-400/25",   bg: "bg-blue-500/10",   text: "text-blue-400",   icon: "text-blue-400" },
      amber:  { border: "border-amber-400/25",   bg: "bg-amber-500/10",  text: "text-amber-400",   icon: "text-amber-400" },
      peach:  { border: "border-rose-400/25",    bg: "bg-rose-500/10",   text: "text-rose-400",    icon: "text-rose-400" },
      luster: { border: "border-luster-400/25",  bg: "bg-luster-500/10", text: "text-luster-400",  icon: "text-luster-400" },
      white:  { border: "border-white/[0.12]",   bg: "bg-white/[0.04]",  text: "text-white/60",    icon: "text-white/40" },
    }

    return (
      <div className="max-w-lg mx-auto space-y-4">
        <div className="space-y-1">
          <p className="font-mono text-[10px] text-white/30 uppercase tracking-widest">Step 1 of 2</p>
          <p className="font-mono text-[11px] text-white/50">
            Pick an activity template to pre-fill the checklist and activity type.
          </p>
          {canManageActivityTypes && (
            <button
              type="button"
              onClick={() => setActivityTypesModalOpen(true)}
              className="mt-1 text-[9px] font-mono font-bold uppercase tracking-wider text-teal-300/70 hover:text-teal-200 transition-colors"
            >
              Manage activity types
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
          {templates.map((t) => {
            const Icon = t.icon
            const palette = TEMPLATE_COLORS[t.color] ?? TEMPLATE_COLORS.white
            const taskCount = t.checklist
              ? Object.values(t.checklist).reduce((sum, cat) => sum + cat.tasks.length, 0)
              : 0
            const isCustom = t.id.startsWith("custom-")
            return (
              <div
                key={t.id}
                className={`group/tpl relative rounded-xl border ${palette.border} ${palette.bg} p-3.5 text-left transition-all duration-150 hover:scale-[1.02] hover:shadow-lg`}
              >
                <button
                  type="button"
                  onClick={() => handleTemplateSelect(t)}
                  className="w-full text-left active:scale-[0.98] transition-transform"
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-8 h-8 rounded-lg border ${palette.border} bg-black/20 flex items-center justify-center shrink-0`}>
                      <Icon className={`w-4 h-4 ${palette.icon}`} />
                    </div>
                    <div className="flex-1 min-w-0 pr-6">
                      <h4 className={`font-mono text-[11px] font-bold uppercase tracking-widest ${palette.text}`}>
                        {t.label}
                      </h4>
                      <p className="font-mono text-[9px] text-white/30 leading-snug mt-0.5 line-clamp-2">
                        {t.description}
                      </p>
                      {taskCount > 0 && (
                        <p className="font-mono text-[8px] text-white/20 mt-1.5">
                          {Object.keys(t.checklist!).length} categories · {taskCount} tasks
                        </p>
                      )}
                    </div>
                  </div>
                </button>
                {canManageTemplates && (
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setEditingTemplate(t) }}
                    className="absolute top-2.5 right-2.5 p-1.5 rounded-lg text-white/20 hover:text-white/60 hover:bg-white/[0.08] opacity-0 group-hover/tpl:opacity-100 transition-all"
                    title={`Edit ${t.label}`}
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            )
          })}

          {/* Create new template card — only for users who can manage templates */}
          {canManageTemplates && (
            <button
              type="button"
              onClick={() => setCreatingNewTemplate(true)}
              className="group/tpl rounded-xl border border-dashed border-white/[0.12] bg-white/[0.02] p-3.5 text-left transition-all duration-150 hover:scale-[1.02] hover:border-violet-400/30 hover:bg-violet-500/[0.04]"
            >
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-lg border border-dashed border-white/[0.12] bg-black/20 flex items-center justify-center shrink-0 group-hover/tpl:border-violet-400/30">
                  <Plus className="w-4 h-4 text-white/25 group-hover/tpl:text-violet-400/70" />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-mono text-[11px] font-bold uppercase tracking-widest text-white/40 group-hover/tpl:text-violet-400">
                    New template
                  </h4>
                  <p className="font-mono text-[9px] text-white/20 leading-snug mt-0.5">
                    Build a custom template for your team.
                  </p>
                </div>
              </div>
            </button>
          )}
        </div>

        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className={`w-full py-2 px-3 rounded-lg border ${glass.border} bg-white/[0.04] text-white/50 font-mono text-[10px] font-bold uppercase tracking-wider hover:bg-white/[0.08] transition-colors`}
          >
            Cancel
          </button>
        )}

        {/* Template editor modal — edit existing */}
        {editingTemplate && (
          <TemplateEditorModal
            initialName={editingTemplate.label}
            initialServiceType={editingTemplate.serviceType ?? ""}
            initialChecklist={editingTemplate.checklist}
            isBuiltIn={!editingTemplate.id.startsWith("custom-")}
            availableServiceTypes={availableServiceTypes}
            onManageActivityTypes={canManageActivityTypes ? () => setActivityTypesModalOpen(true) : undefined}
            onAddActivityType={canManageActivityTypes ? async (name) => { await saveActivityTypes([...availableServiceTypes, name]) } : undefined}
            onUse={(name, st, cl) => {
              const tpl: ActivityTemplate = { ...editingTemplate, label: name, serviceType: st || undefined, checklist: cl }
              setEditingTemplate(null)
              handleTemplateSelect(tpl)
            }}
            onSave={canManageTemplates ? async (name, st, cl) => { await saveCustomTemplate(name, st, cl) } : undefined}
            onDelete={canManageTemplates && editingTemplate.id.startsWith("custom-")
              ? async () => { await deleteCustomTemplate(editingTemplate.label) }
              : undefined
            }
            onClose={() => setEditingTemplate(null)}
          />
        )}

        {/* Template editor modal — create new */}
        {creatingNewTemplate && (
          <TemplateEditorModal
            availableServiceTypes={availableServiceTypes}
            onManageActivityTypes={canManageActivityTypes ? () => setActivityTypesModalOpen(true) : undefined}
            onAddActivityType={canManageActivityTypes ? async (name) => { await saveActivityTypes([...availableServiceTypes, name]) } : undefined}
            onUse={(name, st, cl) => {
              const tpl: ActivityTemplate = {
                id: `custom-${name}`,
                label: name,
                description: `Custom template: ${name}`,
                icon: ClipboardList,
                serviceType: st || undefined,
                checklist: cl,
                color: "luster",
              }
              setCreatingNewTemplate(false)
              handleTemplateSelect(tpl)
            }}
            onSave={canManageTemplates ? async (name, st, cl) => { await saveCustomTemplate(name, st, cl) } : undefined}
            onClose={() => setCreatingNewTemplate(false)}
          />
        )}
        {activityTypesModal}
      </div>
    )
  }

  // ── Step 2: Activity form ──────────────────────────────────────────────────

  return (
    <div className="max-w-lg mx-auto">
      {/* Back to template picker (admin only) */}
      {isAdmin && (
        <button
          type="button"
          onClick={() => setStep("template")}
          className="flex items-center gap-1.5 mb-4 font-mono text-[10px] text-white/40 hover:text-white/70 transition-colors"
        >
          <ChevronLeft className="w-3.5 h-3.5" />
          {selectedTemplate ? selectedTemplate.label : "Change template"}
        </button>
      )}

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* 1. Property selection */}
        <div>
          <label className="block text-[10px] font-mono font-bold text-white/30 uppercase tracking-widest mb-1.5 flex items-center gap-2">
            <Building2 className="w-4 h-4" />
            Property selection *
          </label>
          <PropertySelector
            properties={propertySelectorOptions}
            groupByClient={isAdmin && clientOptions.length > 0}
            clientOptions={isAdmin ? clientOptions : undefined}
            value={propertyId || null}
            onChange={(id) => setPropertyId(id ?? "")}
            placeholder="Select building..."
            aria-label="Select property"
          />
          {!propertyId && (
            <p className="mt-1 font-mono text-[10px] text-amber-400/60">Required</p>
          )}
        </div>

        {/* Admin-only: title, service type, date, status, operator */}
        {isAdmin && (
          <div className={`space-y-4 rounded-2xl border ${glass.border} bg-white/[0.04] p-4`}>
            <div>
              <label className="block text-[10px] font-mono font-bold text-white/30 uppercase tracking-widest mb-1.5 flex items-center gap-2">
                <FileText className="w-4 h-4" />
                Activity title
              </label>
              <input
                type="text"
                value={reportTitle}
                onChange={(e) => setReportTitle(e.target.value)}
                className={`w-full px-3 py-2 rounded-lg border ${glass.border} bg-white/[0.04] text-white/80 font-mono text-[11px] focus:outline-none focus:ring-2 focus:ring-teal-400/30 placeholder:text-white/20`}
                placeholder="Leave blank to autofill on save"
              />
              {(() => {
                const suggested = properties.find((p) => p.id === propertyId)
                  ? `${properties.find((p) => p.id === propertyId)!.name} – ${formatRelativeDate(cleanDate)}`
                  : null
                return suggested ? (
                  <button
                    type="button"
                    onClick={() => setReportTitle(suggested)}
                    className="mt-1.5 flex items-center gap-1.5 text-[10px] font-mono text-teal-400/60 hover:text-teal-300 transition-colors group"
                  >
                    <span className="text-teal-400/40 group-hover:text-teal-300 transition-colors">✦</span>
                    <span>Autofill</span>
                    <span className="text-white/25 group-hover:text-white/50 transition-colors">"{suggested}"</span>
                  </button>
                ) : null
              })()}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-[10px] font-mono font-bold text-white/30 uppercase tracking-widest mb-1.5">
                  Activity type
                </label>
                {templateLocksServiceType ? (
                  <div className="space-y-1.5">
                    <button
                      type="button"
                      onClick={() => setStep("template")}
                      className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg border ${glass.borderLight} bg-white/[0.02] text-left hover:border-teal-400/30 hover:bg-teal-500/[0.04] transition-colors`}
                    >
                      <span className="w-2 h-2 rounded-full bg-teal-400/60 shrink-0" />
                      <span className="font-mono text-[11px] text-white/75 truncate">{serviceType}</span>
                    </button>
                    <p className="font-mono text-[9px] text-white/35">
                      Locked by template. Use back/change template to switch activity type.
                    </p>
                  </div>
                ) : (
                  <div className="relative">
                    <div className="flex items-center gap-2">
                      <div className="flex-1">
                        <GlitchSelect
                          value={serviceType ?? availableServiceTypes[0] ?? null}
                          onChange={(v) => setServiceType(v)}
                          options={availableServiceTypes.map((t) => ({ value: t, label: t }))}
                          placeholder="Select type…"
                          aria-label="Activity type"
                        />
                      </div>
                      {canManageActivityTypes && (
                        <button
                          type="button"
                          onClick={() => { setAddingType(v => !v); setNewTypeDraft("") }}
                          className={`shrink-0 w-7 h-7 flex items-center justify-center rounded-lg border ${glass.border} bg-white/[0.04] text-white/40 hover:text-white/80 hover:border-teal-400/30 transition-colors`}
                          aria-label="Add activity type"
                        >
                          <Plus className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                    {addingType && (
                      <div className={`absolute top-full left-0 right-0 mt-1 z-50 rounded-xl border ${glass.border} bg-black/90 backdrop-blur-xl p-3 space-y-2 shadow-xl`}>
                        <input
                          autoFocus
                          value={newTypeDraft}
                          onChange={e => setNewTypeDraft(e.target.value)}
                          onKeyDown={e => { if (e.key === "Enter") handleAddType(); if (e.key === "Escape") setAddingType(false) }}
                          placeholder="New type name\u2026"
                          className={`w-full bg-transparent font-mono text-[12px] text-white/80 border-b ${glass.border} pb-1 outline-none placeholder:text-white/20`}
                        />
                        <div className="flex items-center gap-2 justify-end">
                          <button type="button" onClick={() => setAddingType(false)} className="font-mono text-[10px] text-white/30 hover:text-white/60 transition-colors">Cancel</button>
                          <button type="button" onClick={handleAddType} disabled={savingType || !newTypeDraft.trim()} className="font-mono text-[10px] text-teal-400 hover:text-teal-300 disabled:opacity-40 transition-colors">
                            {savingType ? "Saving\u2026" : "Add"}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div>
                <label className="block text-[10px] font-mono font-bold text-white/30 uppercase tracking-widest mb-1.5">Date conducted</label>
                <GlitchDatePicker
                  value={cleanDate}
                  onChange={(v) => setCleanDate(v)}
                  aria-label="Date conducted"
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-[10px] font-mono font-bold text-white/30 uppercase tracking-widest mb-1.5">Status</label>
                <GlitchSelect
                  value={status ?? "Completed"}
                  onChange={(v) => setStatus(v as CleanReport["status"])}
                  options={[
                    { value: "Scheduled", label: "Scheduled" },
                    { value: "In Progress", label: "In Progress" },
                    { value: "Completed", label: "Completed" },
                  ]}
                  aria-label="Status"
                />
                {(() => {
                  const statusValue = status ?? "Completed"
                  return (
                    <div
                      className={`mt-2 inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[10px] font-mono font-bold uppercase tracking-wider ${STATUS_PREVIEW_CLASSES[statusValue] ?? "bg-white/[0.04] text-white/50 border-white/[0.12]"}`}
                    >
                      {statusValue === "Completed" ? (
                    <CheckCircle2 className="w-3 h-3 shrink-0" />
                      ) : statusValue === "Scheduled" ? (
                    <Calendar className="w-3 h-3 shrink-0" />
                      ) : (
                    <Clock3 className="w-3 h-3 shrink-0" />
                      )}
                      <span>{statusValue}</span>
                    </div>
                  )
                })()}
              </div>
              <div>
                <label className="block text-[10px] font-mono font-bold text-white/30 uppercase tracking-widest mb-1.5">Operator</label>
                <GlitchSelect
                  value={operatorChoice}
                  onChange={(v) => setOperatorChoice(v as "none" | string | "other")}
                  options={[
                    { value: "none", label: "— None" },
                    ...operators.map((c) => ({ value: c.id, label: c.displayName })),
                    { value: "other", label: "Other (enter name)" },
                  ]}
                  icon={<User className="w-3.5 h-3.5" />}
                  aria-label="Operator"
                />
                {operatorChoice === "other" && (
                  <input
                    type="text"
                    value={operatorNameCustom}
                    onChange={(e) => setOperatorNameCustom(e.target.value)}
                    className={`w-full mt-2 px-3 py-2 rounded-lg border ${glass.border} bg-white/[0.04] text-white/80 font-mono text-[11px] focus:outline-none focus:ring-2 focus:ring-teal-400/30 placeholder:text-white/40`}
                    placeholder="Staff member name"
                  />
                )}
              </div>
            </div>
          </div>
        )}

        {/* 2. Supply & site check */}
        <button
          type="button"
          onClick={() => setSupplySiteModalOpen(true)}
          className={`w-full flex items-center justify-between gap-3 p-4 rounded-2xl border ${glass.border} bg-white/[0.04] hover:bg-white/[0.08] transition-colors text-left`}
        >
          <span className="flex items-center gap-2 text-[11px] font-mono font-bold text-white/80">
            <Package className="w-5 h-5 text-teal-400/90 shrink-0" />
            Supply & Site Check
          </span>
          <span className="flex items-center gap-2">
            {(supplySnapPhotos.length > 0 || siteSenseAudioUrl) && (
              <span className="text-[9px] font-mono text-teal-400/70 tabular-nums">
                {[supplySnapPhotos.length > 0, !!siteSenseAudioUrl].filter(Boolean).length}/2
              </span>
            )}
            <ChevronRight className="w-5 h-5 text-white/40 shrink-0" aria-hidden />
          </span>
        </button>

        {/* 3. Photos */}
        <button
          type="button"
          onClick={() => setPhotosModalOpen(true)}
          className={`w-full flex items-center justify-between gap-3 p-4 rounded-2xl border ${glass.border} bg-white/[0.04] hover:bg-white/[0.08] transition-colors text-left`}
        >
          <span className="flex items-center gap-2 text-[11px] font-mono font-bold text-white/80">
            <ImagePlus className="w-5 h-5 text-teal-400/90 shrink-0" />
            Photos
          </span>
          <span className="text-[10px] font-mono text-white/40">
            {photos.length} photo{photos.length !== 1 ? "s" : ""}
          </span>
          <ChevronRight className="w-5 h-5 text-white/40 shrink-0" aria-hidden />
        </button>

        {/* 4. Checklist */}
        <button
          type="button"
          onClick={() => setChecklistModalOpen(true)}
          className={`w-full flex items-center justify-between gap-3 p-4 rounded-2xl border ${glass.border} bg-white/[0.04] hover:bg-white/[0.08] transition-colors text-left`}
        >
          <span className="flex items-center gap-2 text-[11px] font-mono font-bold text-white/80">
            <ClipboardList className="w-5 h-5 text-teal-400/90 shrink-0" />
            Checklist
          </span>
          {categoryKeys.length > 0 ? (
            <span className="text-[10px] font-mono text-white/40">
              {categoryKeys.length} categor{categoryKeys.length === 1 ? "y" : "ies"} ·{" "}
              {categoryKeys.reduce((acc, cat) => acc + (checklist[cat]?.tasks?.length ?? 0), 0)} tasks
            </span>
          ) : (
            <span className="text-[10px] font-mono text-white/20">Empty — tap to add</span>
          )}
          <ChevronRight className="w-5 h-5 text-white/40 shrink-0" aria-hidden />
        </button>

        {/* 5. Notes (rich text) — last content field before submit */}
        <div>
          <label className="block text-[10px] font-mono font-bold text-white/30 uppercase tracking-widest mb-1.5 flex items-center gap-2">
            <FileText className="w-4 h-4" />
            {selectedTemplate?.id === "blank" ? "Activity body" : "Notes"}
          </label>
          <RichTextEditor
            content={notes}
            onChange={setNotes}
            placeholder={selectedTemplate?.id === "blank" ? "Describe this activity…" : "Any notes about this activity…"}
          />
        </div>

        <div className="flex gap-3 pt-2">
          {isAdmin && onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className={`flex-1 py-2 px-3 rounded-lg border ${glass.border} bg-white/[0.04] text-white/50 font-mono text-[10px] font-bold uppercase tracking-wider hover:bg-white/[0.08] transition-colors`}
            >
              Cancel
            </button>
          )}
          <button
            type="submit"
            disabled={saving}
            className={`flex items-center justify-center gap-2 py-2 px-4 rounded-lg border border-teal-400/25 bg-teal-500/20 text-teal-400 font-mono text-[10px] font-bold uppercase tracking-wider transition-colors disabled:opacity-60 hover:bg-teal-500/30 ${isAdmin && onCancel ? "flex-1" : "w-full"}`}
          >
            {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
            {isAdmin ? "Create activity" : "Submit activity"}
          </button>
        </div>
      </form>

      {/* Supply & Site Check modal */}
      {supplySiteModalOpen &&
        createPortal(
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <div
              className="absolute inset-0 glass-backdrop"
              onClick={() => setSupplySiteModalOpen(false)}
              aria-hidden
            />
            <div className={`relative w-full max-w-lg max-h-[90vh] flex flex-col ${glass.cardBg} backdrop-blur-2xl rounded-2xl shadow-[0_32px_80px_-16px_rgba(0,0,0,0.6)] border ${glass.border} overflow-hidden`}>
              {/* Terminal header */}
              <div className={`shrink-0 px-4 py-3 border-b ${glass.borderLight}`}>
                <div className="flex items-center justify-between">
                  <div className="flex flex-col gap-0.5">
                    <h3 className="text-[11px] font-mono font-bold text-white/90 uppercase tracking-widest flex items-center gap-2">
                      <Package className="w-4 h-4 text-teal-400/90" />
                      Supply & Site Check
                    </h3>
                    <div className="font-mono text-[9px] flex items-center gap-1.5">
                      <span className="text-white/15">0x03</span>
                      <span className="text-white/10">&middot;</span>
                      <span className="text-teal-400/60">
                        <GlitchWord word="synapse" intensity={1} colorBase="teal" />.scan()
                      </span>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setSupplySiteModalOpen(false)}
                    className="p-1.5 rounded-lg text-white/30 hover:text-white/70 hover:bg-white/[0.06] transition-colors"
                    aria-label="Close"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-5">
                {/* Section: Room / Location */}
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-[10px] font-mono font-bold text-white/30 uppercase tracking-widest">Location</span>
                    {supplySnapRoom && <span className="w-1.5 h-1.5 rounded-full bg-teal-400/60" />}
                  </div>
                  <input
                    type="text"
                    value={supplySnapRoom}
                    onChange={(e) => setSupplySnapRoom(e.target.value)}
                    className={`w-full px-3 py-2 rounded-lg border ${glass.border} bg-white/[0.04] text-white/80 font-mono text-[11px] focus:outline-none focus:ring-2 focus:ring-teal-400/30 placeholder:text-white/25`}
                    placeholder="e.g. Main closet, Break room"
                  />
                </div>

                {/* Section: Supply Photos */}
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-[10px] font-mono font-bold text-white/30 uppercase tracking-widest">Supply closet</span>
                    {supplySnapPhotos.length > 0 && (
                      <>
                        <span className="w-1.5 h-1.5 rounded-full bg-teal-400/60" />
                        <span className="text-[9px] font-mono text-white/20 tabular-nums">{supplySnapPhotos.length}/5</span>
                      </>
                    )}
                  </div>
                  {supplySnapPhotos.length > 0 ? (
                    <div className={`rounded-xl border ${glass.border} bg-white/[0.03] overflow-hidden`}>
                      <div className="flex items-center gap-1.5 p-2 overflow-x-auto scrollbar-none">
                        {supplySnapPhotos.map((url, i) => (
                          <div key={url} className={`relative shrink-0 w-20 h-20 rounded-lg overflow-hidden border ${glass.border} group`}>
                            <img src={url} alt="" className="w-full h-full object-cover" />
                            <button
                              type="button"
                              onClick={() => setSupplySnapPhotos((prev) => prev.filter((_, j) => j !== i))}
                              className="absolute inset-0 flex items-center justify-center bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <Trash2 className="w-3.5 h-3.5 text-red-400" />
                            </button>
                          </div>
                        ))}
                      </div>
                      <div className={`flex items-center justify-between px-3 py-2 border-t ${glass.borderLight}`}>
                        <span className="font-mono text-[11px] text-white/50">
                          Capacity: <span className="text-teal-400 font-bold">{supplySnapEstimate ?? 50}%</span>
                        </span>
                        <div className="flex items-center gap-2">
                          {supplySnapPhotos.length < 5 && (
                            <button
                              type="button"
                              onClick={() => setSupplySnapOpen(true)}
                              className="text-[10px] font-mono text-white/40 hover:text-teal-400/80 transition-colors"
                            >
                              + Add more
                            </button>
                          )}
                          <span className="text-white/10">&middot;</span>
                          <button
                            type="button"
                            onClick={() => { setSupplySnapPhotos([]); setSupplySnapEstimate(null) }}
                            className="text-[10px] font-mono text-white/40 hover:text-red-400 transition-colors"
                          >
                            Clear all
                          </button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setSupplySnapOpen(true)}
                      className={`w-full flex items-center justify-center gap-2 px-4 py-4 rounded-xl border border-dashed ${glass.border} text-white/30 font-mono text-[11px] hover:border-teal-400/30 hover:text-teal-400/70 transition-colors`}
                    >
                      <Camera className="w-4 h-4" />
                      Add supply closet photos
                    </button>
                  )}
                </div>

                {/* Section: Site Walkthrough */}
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-[10px] font-mono font-bold text-white/30 uppercase tracking-widest">Site walkthrough</span>
                    {siteSenseAudioUrl && <span className="w-1.5 h-1.5 rounded-full bg-teal-400/60" />}
                  </div>
                  <SiteSenseBlock
                    audioUrl={siteSenseAudioUrl}
                    onAudioUrlChange={setSiteSenseAudioUrl}
                    disabled={saving}
                    uploadPrefix={uploadPrefix}
                  />
                </div>

              </div>

              {/* Footer */}
              <div className={`shrink-0 px-4 py-3 border-t ${glass.borderLight}`}>
                <button
                  type="button"
                  onClick={() => setSupplySiteModalOpen(false)}
                  className="w-full py-2.5 px-4 rounded-lg border border-teal-400/25 bg-teal-500/15 text-teal-400 font-mono text-[10px] font-bold uppercase tracking-wider hover:bg-teal-500/25 transition-colors"
                >
                  Done
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      {/* Photos modal */}
      {photosModalOpen &&
        createPortal(
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <div
              className={`absolute inset-0 ${glass.modalBg} backdrop-blur-sm`}
              onClick={() => setPhotosModalOpen(false)}
              aria-hidden
            />
            <div className={`relative w-full max-w-lg max-h-[90vh] flex flex-col ${glass.cardBg} backdrop-blur-2xl rounded-2xl shadow-xl border ${glass.border} overflow-hidden`}>
              <div className={`flex items-center justify-between shrink-0 px-4 py-3 border-b ${glass.borderLight}`}>
                <h3 className="text-base font-mono font-bold text-white/90 uppercase tracking-widest flex items-center gap-2">
                  <ImagePlus className="w-5 h-5 text-teal-400/90" />
                  Photos
                </h3>
                <button
                  type="button"
                  onClick={() => setPhotosModalOpen(false)}
                  className="p-1.5 rounded-lg text-white/40 hover:text-white/80 hover:bg-white/10"
                  aria-label="Close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-4">
                <button
                  type="button"
                  onClick={() => setMediaModalOpen(true)}
                  className={`w-full flex items-center justify-center gap-2 px-4 py-4 rounded-xl border border-dashed ${glass.border} text-white/40 font-mono text-[11px] hover:border-teal-400/30 hover:text-teal-400/80 transition-colors`}
                >
                  <ImagePlus className="w-5 h-5" />
                  Add photos (camera or gallery)
                </button>
                {photos.length > 0 && (
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                    {photos.map((url, i) => (
                      <div key={i} className={`relative aspect-square rounded-lg overflow-hidden bg-white/[0.04] border ${glass.border} group`}>
                        <img src={url} alt="" className="w-full h-full object-cover" />
                        <button
                          type="button"
                          onClick={() => removePhoto(i)}
                          className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity text-white text-[11px] font-mono"
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className={`shrink-0 p-4 border-t ${glass.borderLight}`}>
                <button
                  type="button"
                  onClick={() => setPhotosModalOpen(false)}
                  className="w-full py-2 px-4 rounded-lg border border-teal-400/25 bg-teal-500/20 text-teal-400 font-mono text-[10px] font-bold uppercase tracking-wider hover:bg-teal-500/30 transition-colors"
                >
                  Done
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      {/* Protocol Checklist modal: admin gets full edit (categories/tasks); operator gets tick-only */}
      {checklistModalOpen && isAdmin && (
        <ChecklistEditModal
          checklist={checklist}
          setChecklist={setChecklist}
          openCategories={openCategories}
          setOpenCategories={setOpenCategories}
          onClose={() => setChecklistModalOpen(false)}
          onReset={protocolChecklist ? () => setChecklist(protocolChecklist) : undefined}
        />
      )}
      {checklistModalOpen && !isAdmin &&
        createPortal(
          <div className="fixed inset-0 z-[110] flex items-center justify-center p-4">
            <div
              className={`absolute inset-0 ${glass.modalBg} backdrop-blur-sm`}
              onClick={() => setChecklistModalOpen(false)}
              aria-hidden
            />
            <div className={`relative w-full max-w-2xl max-h-[85vh] flex flex-col ${glass.cardBg} backdrop-blur-2xl rounded-2xl shadow-xl border ${glass.border} overflow-hidden`}>
              <div className={`flex items-center justify-between shrink-0 px-4 py-3 border-b ${glass.borderLight}`}>
                <h3 className="text-base font-mono font-bold text-white/90 uppercase tracking-widest flex items-center gap-2">
                  <ClipboardList className="w-5 h-5 text-teal-400/90" />
                  Protocol checklist
                </h3>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setAllOpen(true)}
                    className="text-[10px] font-mono text-white/50 hover:text-white/80 px-2 py-1 rounded transition-colors"
                  >
                    Expand all
                  </button>
                  <button
                    type="button"
                    onClick={() => setAllOpen(false)}
                    className="text-[10px] font-mono text-white/50 hover:text-white/80 px-2 py-1 rounded transition-colors"
                  >
                    Collapse all
                  </button>
                  <button
                    type="button"
                    onClick={() => setChecklistModalOpen(false)}
                    className="p-1.5 rounded-lg text-white/40 hover:text-white/80 hover:bg-white/10"
                    aria-label="Close"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>
              <div className="flex-1 min-h-0 overflow-y-auto p-4">
                <p className="text-[11px] font-mono text-white/40 mb-3">
                  Tick each task as you complete it. This serves as your reminder and proof for admins and clients.
                </p>
                <div className={`rounded-xl border ${glass.border} ${glass.cardBg} overflow-hidden`}>
                  {Object.entries(checklist).map(([category, data]) => {
                    const isOpen = isCategoryOpen(category)
                    const completedCount = data.tasks.filter((t) => t.completed).length
                    const setCategoryCompleted = (completed: boolean) => {
                      setChecklist((prev) => {
                        const next = { ...prev }
                        next[category] = {
                          ...next[category],
                          tasks: next[category].tasks.map((t) => ({ ...t, completed })),
                        }
                        return next
                      })
                    }
                    const allDone = completedCount === data.tasks.length
                    return (
                      <div key={category} className={`border-b ${glass.borderLight} last:border-b-0`}>
                        <button
                          type="button"
                          onClick={() => setOpenCategories((p) => ({ ...p, [category]: !p[category] }))}
                          className="w-full flex items-center justify-between px-4 py-3 hover:bg-white/[0.04] transition-colors text-left"
                        >
                          <span className="flex items-center gap-2 font-mono text-[11px] font-bold text-white/80">
                            {isOpen ? <ChevronDown className="w-4 h-4 shrink-0" /> : <ChevronRight className="w-4 h-4 shrink-0" />}
                            {allDone && <Check className="w-4 h-4 shrink-0 text-emerald-400" strokeWidth={3} />}
                            {category}
                            <span className="text-[10px] font-normal text-white/40">
                              ({completedCount}/{data.tasks.length})
                            </span>
                          </span>
                        </button>
                        {isOpen && (
                          <div className="px-4 pb-3 pt-0 space-y-1.5">
                            {data.tasks.map((task, idx) => (
                              <label
                                key={idx}
                                className={`flex items-center gap-3 py-2 px-3 rounded-lg cursor-pointer text-[11px] font-mono transition-colors ${
                                  task.completed
                                    ? `bg-white/[0.04] border ${glass.border} text-white/80`
                                    : "bg-transparent border border-white/[0.04] text-white/40 hover:bg-white/[0.02]"
                                }`}
                              >
                                <span
                                  className={`flex shrink-0 w-5 h-5 rounded-full flex items-center justify-center ${
                                    task.completed ? "bg-emerald-500/80" : "border-2 border-white/20"
                                  }`}
                                >
                                  {task.completed && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
                                </span>
                                <input
                                  type="checkbox"
                                  checked={task.completed}
                                  onChange={() => {
                                    setChecklist((prev) => {
                                      const next = { ...prev }
                                      const cat = { ...next[category], tasks: [...next[category].tasks] }
                                      cat.tasks[idx] = { ...cat.tasks[idx], completed: !cat.tasks[idx].completed }
                                      next[category] = cat
                                      return next
                                    })
                                  }}
                                  className="sr-only"
                                />
                                <span className="font-bold">{task.task}</span>
                              </label>
                            ))}
                            <div
                              className={`flex items-center gap-2 pt-2 border-t ${glass.borderLight} mt-2`}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <button
                                type="button"
                                onClick={() => setCategoryCompleted(true)}
                                className={`px-3 py-1.5 rounded-lg text-[10px] font-mono text-white/50 bg-white/[0.04] border ${glass.border} hover:bg-white/[0.08] transition-colors`}
                              >
                                Check all
                              </button>
                              <button
                                type="button"
                                onClick={() => setCategoryCompleted(false)}
                                className={`px-3 py-1.5 rounded-lg text-[10px] font-mono text-white/50 bg-white/[0.04] border ${glass.border} hover:bg-white/[0.08] transition-colors`}
                              >
                                Uncheck all
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>
              <div className={`shrink-0 flex gap-3 justify-end p-4 border-t ${glass.borderLight}`}>
                <button
                  type="button"
                  onClick={() =>
                    setChecklist((prev) => {
                      const next = { ...prev }
                      categoryKeys.forEach((cat) => {
                        next[cat] = { tasks: (next[cat]?.tasks ?? []).map((t) => ({ ...t, completed: true })) }
                      })
                      return next
                    })
                  }
                  className={`py-2 px-3 rounded-lg border ${glass.border} bg-white/[0.04] text-white/50 font-mono text-[10px] font-bold uppercase tracking-wider hover:bg-white/[0.08] transition-colors`}
                >
                  Check all
                </button>
                <button
                  type="button"
                  onClick={() =>
                    setChecklist((prev) => {
                      const next = { ...prev }
                      categoryKeys.forEach((cat) => {
                        next[cat] = { tasks: (next[cat]?.tasks ?? []).map((t) => ({ ...t, completed: false })) }
                      })
                      return next
                    })
                  }
                  className={`py-2 px-3 rounded-lg border ${glass.border} bg-white/[0.04] text-white/50 font-mono text-[10px] font-bold uppercase tracking-wider hover:bg-white/[0.08] transition-colors`}
                >
                  Uncheck all
                </button>
                <button
                  type="button"
                  onClick={() => setChecklistModalOpen(false)}
                  className="py-2 px-4 rounded-lg border border-teal-400/25 bg-teal-500/20 text-teal-400 font-mono text-[10px] font-bold uppercase tracking-wider hover:bg-teal-500/30 transition-colors"
                >
                  Done
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      <MediaCaptureModal
        open={mediaModalOpen}
        onClose={() => setMediaModalOpen(false)}
        onPhotosAdded={handlePhotosAdded}
        uploadPrefix={uploadPrefix}
      />
      <SupplySnapModal
        open={supplySnapOpen}
        onClose={() => setSupplySnapOpen(false)}
        onComplete={({ urls, estimate }) => {
          setSupplySnapPhotos((prev) => [...prev, ...urls])
          setSupplySnapEstimate(estimate)
          setSupplySnapOpen(false)
        }}
        initialEstimate={supplySnapEstimate ?? undefined}
        maxPhotos={5 - supplySnapPhotos.length}
        uploadPrefix={uploadPrefix}
      />
      {activityTypesModal}
    </div>
  )
}
